import { jwtDecode } from 'jwt-decode';
import { Account, Profile, User } from 'next-auth';
import { AdapterUser } from 'next-auth/adapters';
import { JWT } from 'next-auth/jwt';

import { logger } from '@lichtblick/logger';

export const refreshAccessToken = async (refreshToken: string) => {
  const logContext = {
    component: 'AuthService',
    operation: 'refreshAccessToken',
  };
  try {
    // The `token_endpoint` can be found in the provider's documentation. Or if they support OIDC,
    // at their `/.well-known/openid-configuration` endpoint.
    // i.e. https://accounts.google.com/.well-known/openid-configuration
    const response = await fetch(`${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/token`, {
      method: 'POST',
      body: new URLSearchParams({
        client_id: process.env.KEYCLOAK_ID ?? '',
        grant_type: 'refresh_token',
        refresh_token: refreshToken ?? '',
      }),
    });

    const tokensOrError = await response.json();

    if (!response.ok) {
      throw tokensOrError;
    }

    const newTokens = tokensOrError as {
      access_token: string;
      refresh_token: string;
    };

    return {
      access_token: newTokens.access_token,
      refresh_token: newTokens.refresh_token,
    };
  } catch (error) {
    // If we fail to refresh the token, return an error so we can handle it on the page
    logger.error('Error refreshing access token', {
      ...logContext,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });

    return {
      error,
    };
  }
};

export const jwtHandler = async ({
  account,
  token,
}: {
  account: Account | null;
  isNewUser?: boolean;
  profile?: Profile | undefined;
  session?: unknown;
  token: JWT;
  trigger?: 'signIn' | 'signUp' | 'update';
  user: User | AdapterUser;
}) => {
  const now = Date.now();

  const nextMidnight = new Date();

  nextMidnight.setUTCHours(23, 59, 59, 999);

  const expiresAt = Math.floor(nextMidnight.getTime() / 1000);

  if (account) {
    return {
      ...token,
      access_token: account.access_token,
      id_token: account.id_token,
      refresh_token: account.refresh_token,
      expiresAt,
      error: token.error,
      errorDetails: token.errorDetails,
    };
  } else {
    if (token.expiresAt && typeof token.expiresAt === 'number' && now > token.expiresAt * 1000) {
      return {};
    }

    const accessToken = jwtDecode(token.access_token ?? '');
    const refreshToken = jwtDecode(token.refresh_token ?? '');

    if ((accessToken?.exp ?? 0) * 1000 <= now) {
      if ((refreshToken?.exp ?? 0) * 1000 >= now && now < expiresAt * 1000) {
        const refreshResult = await refreshAccessToken(token.refresh_token ?? '');

        return {
          ...token,
          access_token: refreshResult.access_token,
          refresh_token: refreshResult.refresh_token,
          expiresAt,
        };
      }
    }
  }

  return token;
};
